import { defineStore } from 'pinia'
import { useNuxtApp } from '#imports'
import {
  type CartItem,
  calcTotalItems,
  calcTotalPrice,
  findExistingItemIndex,
  validateCartItem,
  sortSupabaseCartRows,
  mergeCartItems,
  applyStockConstraints,
} from '~/stores/cart-utils'

const getSupabaseClient = async () => {
  const { useSupabaseClient } = await import('~/composables/useSupabaseClient')
  return useSupabaseClient()
}

interface CartState {
  items: CartItem[]
  isOpen: boolean
  selectedSize: string | null
  selectedSizeHand: string | null
  isSyncing: boolean
  justAddedItem: boolean // Flag to prevent reload after adding item locally
}

export const useCartStore = defineStore('cart', {
  state: (): CartState => ({
    items: [],
    isOpen: false,
    selectedSize: null,
    selectedSizeHand: null,
    isSyncing: false,
    justAddedItem: false
  }),

  getters: {
    totalItems: (state) => calcTotalItems(state.items),
    totalPrice: (state) => calcTotalPrice(state.items),
    formattedTotalPrice: (state) => {
      const total = calcTotalPrice(state.items)
      // Use browser locale for formatting
      return new Intl.NumberFormat(navigator.language || 'en-US', {
        style: 'currency',
        currency: 'EUR'
      }).format(total)
    }
  },

  actions: {
    addItem(item: CartItem) {
      const validationError = validateCartItem(item)
      if (validationError) {
        console.error('🛒 [Cart] Cannot add item:', validationError, item)
        return
      }

      console.log('🛒 [Cart] Adding item:', {
        id: item.id,
        product_id: item.product_id,
        title: item.title,
        size: item.size,
        quantity: item.quantity
      })

      const existingItemIndex = findExistingItemIndex(this.items, item)

      if (existingItemIndex !== -1) {
        // Item existe déjà, incrémenter la quantité
        this.items[existingItemIndex].quantity += item.quantity
      } else {
        // Nouvel item, l'ajouter en haut de la liste (newest first)
        this.items.unshift(item)
        // Set flag to prevent reload from Supabase immediately after adding
        this.justAddedItem = true
        // Clear flag after sync completes (async)
        setTimeout(() => {
          this.justAddedItem = false
        }, 2000) // Give enough time for sync to complete
      }
      
      // Synchroniser avec Supabase
      this.syncToSupabase()
    },

    removeItem(itemId: string) {
      this.items = this.items.filter(item => item.id !== itemId)
      
      if (process.client) {
        this.syncToSupabase()
      }
    },

    updateQuantity(itemId: string, quantity: number) {
      const item = this.items.find(i => i.id === itemId)
      if (item) {
        if (quantity <= 0) {
          this.removeItem(itemId)
        } else {
          item.quantity = quantity
          if (process.client) {
            this.syncToSupabase()
          }
        }
      }
    },

    clearCart() {
      this.items = []
      
      if (process.client) {
        this.syncToSupabase()
      }
    },

    async forceClearCart() {
      this.items = []
      
      if (process.client) {
        const client = await getSupabaseClient()
        const { data: { user } } = await client.auth.getUser()
        if (user) {
          try {
            await client
              .from('cart')
              .delete()
              .eq('user_id', user.id)
          } catch (error) {
            console.error('🛒 [Cart] Error force clearing cart:', error)
          }
        }
      }
    },

    toggleCart() {
      this.isOpen = !this.isOpen
    },

    setSelectedSize(size: string) {
  
      this.selectedSize = size
    },

    setSelectedSizeHand(size: string) {
      this.selectedSizeHand = size
    },

    clearSelectedSize() {
      this.selectedSize = null
    },

    clearSelectedSizeHand() {
      this.selectedSizeHand = null
    },

    // Supabase sync methods
    async loadFromSupabase() {
      if (!process.client) return
      
      // Don't reload if we just added an item locally (prevents reordering)
      if (this.justAddedItem) {
        console.log('🔄 [Cart Sync] Skipping load - item just added locally')
        return
      }
      
      const client = await getSupabaseClient()
      const { data: { user } } = await client.auth.getUser()
      
      // Use session_id for anonymous users
      const sessionId = !user ? this.getSessionId() : null
      
      if (!user && !sessionId) {
        console.log('🔄 [Cart Sync] No user or session, skipping load')
        return
      }
  
      try {
        this.isSyncing = true
        
        // Load by user_id or session_id, ordered by newest first
        const query = user
          ? client.from('cart').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).order('id', { ascending: false })
          : client.from('cart').select('*').eq('session_id', sessionId!).order('created_at', { ascending: false }).order('id', { ascending: false })
        
        const { data, error } = await query
        
        if (error) {
          console.error('🔄 [Cart Sync] Error loading cart:', error)
          return
        }

        if (data && data.length > 0) {
          const sortedData = sortSupabaseCartRows(data)
          
          const newItems = sortedData.map(item => ({
            id: item.id || item.title, // Use title as fallback if id is missing
            product_id: item.product_id,
            title: item.title,
            price: parseFloat(item.price),
            quantity: item.quantity,
            image: item.image,
            size: item.size,
            color: item.color,
            type: item.type
          }))

          // Only update if items are different to avoid unnecessary syncs
          // Check both content and order
          const itemsChanged = JSON.stringify(this.items) !== JSON.stringify(newItems)
          if (itemsChanged) {
            // Preserve local order if items were just added (check if local items exist)
            // Only replace if loading fresh from Supabase (no local items or different count)
            if (this.items.length === 0 || this.items.length !== newItems.length) {
              this.items = newItems
            } else {
              this.items = mergeCartItems(this.items, newItems)
            }
            // Validate quantities against available stock
            await this.validateQuantities()
          }
        } else {
          // Ne pas vider le panier local si Supabase est vide - le panier local est la source de vérité
          console.log('🔄 [Cart Sync] No data in Supabase, keeping local cart items')
          // Ne pas vider : this.items reste intact
        }
      } catch (error) {
        console.error('🔄 [Cart Sync] Error loading cart:', error)
      } finally {
        this.isSyncing = false
      }
    },

    // Get or create session ID for anonymous users
    getSessionId(): string {
      if (!process.client) return ''
      let sessionId = localStorage.getItem('cart_session_id')
      if (!sessionId) {
        // Generate a unique session ID
        sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem('cart_session_id', sessionId)
      }
      return sessionId
    },

    async syncToSupabase() {
      if (!process.client) return
      
      // Prevent multiple simultaneous syncs
      if (this.isSyncing) {
        console.log('🔄 [Cart Sync] Already syncing, skipping')
        return
      }
      
      const client = await getSupabaseClient()
      const { data: { user } } = await client.auth.getUser()
      
      // Use session_id for anonymous users
      const sessionId = !user ? this.getSessionId() : null
      
      if (!user && !sessionId) {
        console.log('🔄 [Cart Sync] No user or session, skipping sync')
        return
      }
      
      try {
        this.isSyncing = true
        
        // Clear existing cart items (by user_id or session_id)
        const deleteQuery = user 
          ? client.from('cart').delete().eq('user_id', user.id)
          : client.from('cart').delete().eq('session_id', sessionId!)
        
        const { error: deleteError } = await deleteQuery
        if (deleteError) {
          console.error('🔄 [Cart Sync] Error clearing cart:', deleteError)
          return
        }
        
        // Insert new cart items in reverse order so newest (first in array) gets highest ID
        // This ensures when loading back, newest items appear first
        if (this.items.length > 0) {
          // Reverse the items array before inserting so the first item (newest) gets inserted last
          // and receives the highest ID, making it appear first when sorted by ID DESC
          const reversedItems = [...this.items].reverse()
          const cartData = reversedItems.map(item => ({
            user_id: user?.id || null,
            session_id: sessionId || null,
            product_id: item.product_id || null,
            title: item.title,
            price: item.price,
            quantity: item.quantity,
            image: item.image,
            size: item.size || null,
            color: item.color || null,
            type: item.type || null
          }))
          const { error: insertError } = await client
            .from('cart')
            .insert(cartData)
          if (insertError) {
            console.error('🔄 [Cart Sync] Error inserting cart items:', insertError)
          } else {
            console.log('🔄 [Cart Sync] Successfully synced', cartData.length, 'items', user ? '(authenticated)' : '(anonymous)')
          }
        }
      } catch (error) {
        console.error('🔄 [Cart Sync] Error syncing cart:', error)
      } finally {
        this.isSyncing = false
      }
    },

    async handleUserLogin() {
      const client = await getSupabaseClient()
      const sessionId = this.getSessionId()
      
      // If user had an anonymous cart, merge it with their user cart
      if (sessionId && this.items.length > 0) {
        console.log('🔄 [Cart] User logged in, merging anonymous cart with user cart', this.items.length)
        
        // Migrate anonymous cart items to user cart
        try {
          // Update session_id carts to user_id
          const { data: { user } } = await client.auth.getUser()
          if (user) {
            const { error: updateError } = await client
              .from('cart')
              .update({ user_id: user.id, session_id: null })
              .eq('session_id', sessionId)
            
            if (updateError) {
              console.error('🔄 [Cart] Error migrating anonymous cart:', updateError)
            } else {
              console.log('🔄 [Cart] Successfully migrated anonymous cart to user cart')
              // Clear session_id from localStorage
              localStorage.removeItem('cart_session_id')
            }
          }
        } catch (error) {
          console.error('🔄 [Cart] Error during cart migration:', error)
        }
      }
      
      // Ne pas charger depuis Supabase si le panier local contient des items
      // Permettre à l'utilisateur de garder son panier local
      if (this.items.length > 0) {
        console.log('🔄 [Cart] Syncing local cart to Supabase', this.items.length)
        // Sync local cart to Supabase instead of replacing it
        await this.syncToSupabase()
      } else {
        await this.loadFromSupabase()
      }
    },

    async handleUserLogout() {
      this.clearCart()
    },

    async validateQuantities() {
      if (!process.client || this.items.length === 0) return
      
      // Prevent sync during validation to avoid loops
      const wasSyncing = this.isSyncing
      this.isSyncing = true
      
      const client = await getSupabaseClient()
      const nuxtApp = useNuxtApp()
      
      // Get online store ID from protected API (allows service role filtering)
      let onlineStore: any = null
      try {
        const stores = await nuxtApp.$fetch<any[]>('/api/stores')
        onlineStore = stores?.find((s: any) => s.type === 'online') || null
      } catch (error) {
        console.error('🛒 [Cart] Error fetching stores from API:', error)
      }
      if (!onlineStore) {
        console.log('🛒 [Cart] No online store found, skipping validation')
        this.isSyncing = wasSyncing
        return
      }
      
      // Get current stock for all items
      const productSkus = this.items.map(item => item.product_id).filter(Boolean)
      if (productSkus.length === 0) {
        console.log('🛒 [Cart] No product SKUs to validate')
        this.isSyncing = wasSyncing
        return
      }
      
      console.log('🛒 [Cart] Validating quantities for SKUs:', productSkus)
      
      const { data: inventory, error } = await client
        .from('inventory')
        .select('*')
        .in('product_sku', productSkus)
        .eq('store_id', onlineStore.id)
      
      if (error) {
        console.error('🛒 [Cart] Error fetching inventory:', error)
        this.isSyncing = wasSyncing
        return
      }
      
      console.log('🛒 [Cart] Inventory data:', inventory)
      
      const { items: adjustedItems, hasChanges } = applyStockConstraints(this.items, inventory || [])
      if (hasChanges) this.items = adjustedItems
      
      // Sync changes back to Supabase if any adjustments were made
      if (hasChanges) {
        // Restore original syncing state before sync
        this.isSyncing = wasSyncing
        await this.syncToSupabase()
      } else {
        // Restore original syncing state if no changes
        this.isSyncing = wasSyncing
      }
    }
  },

  persist: true
})
